import * as React from "react";
import {
  Outlet,
  isRouteErrorResponse,
  useLocation,
  useRouteError,
} from "react-router-dom";
import { useUISlice } from "@/models/useUISlice";
import { useToast } from "@/models/useToast";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import { getPlatform, isNativeApp } from "@/platform";
import { useAppStore } from "@/controller";
import { useAuth } from "@/models/useAuth";
import { useTheme, updateStatusBarStyle } from "@/models/useTheme";
import { logBrowserCrash, logClientEvent } from "@/telemetry";
import { actions, config, lifecycle, telemetry } from "@/controller";
import { useNavigate } from "@/_router";
import AlertWidgetCreateTranscriptFromFileImport from "@/components/AlertWdigetCreateTranscriptFromFileImport";
import AlertModal from "@/components/AlertModal";
import AlertPrompt from "@/components/AlertPrompt";
import AlertPromptSignIn from "@/components/AlertPromptSignIn";
import AlertTagPicker from "@/components/AlertTagPicker";
import AlertWidgetCreateTranscriptVoiceRecording from "@/components/AlertWidgetCreateTranscriptVoiceRecording";
import AlertWidgetSearch from "@/components/AlertWidgetSearch";
import AlertLanguagePicker from "@/components/AlertLanguagePicker";
import AlertLargeFileUpload from "@/components/AlertLargeFileUpload";
import AlertWidgetPaywall from "@/components/AlertWidgetPaywall";
import AlertExportTranscriptOptions from "@/components/AlertExportTranscriptOptions";
import AlertWidgetOnboarding from "@/components/AlertWidgetOnboarding";
import AlertFlowSessionArmed from "@/components/AlertFlowSessionArmed";
import DesktopRuntimeBridge from "@/components/DesktopRuntimeBridge";
import { DesktopHostProvider } from "@/models/DesktopHostProvider";
import { attachBrowserBack, attachViewportResize } from "@/dom-events";
import { accountEmail, hasAccountIdentity } from "@/auth-identity";
import {
  APP_DOWNLOAD_URLS,
  getAppsWebUrl,
  getDeviceAppPlatform,
} from "@/product-links";
import { fileOpen, type OpenedSharedFile } from "@/clients/file-open.plugin";
import { desktop, runtime } from "@/controller";
import { Navigate } from "@/router";

const MOBILE_MAX_WIDTH = 768;
const DictationLauncher = React.lazy(
  () => import("@/components/DictationLauncher"),
);

const loadAnalytics = () => {
  if (getPlatform() === "web") {
    telemetry.pageview(window.location.pathname);
  }
};

const getRouteErrorMessage = (routeError: unknown) => {
  if (isRouteErrorResponse(routeError)) {
    return routeError.statusText || String(routeError.data || routeError.status);
  }

  if (routeError instanceof Error) {
    return routeError.message;
  }

  return String(routeError || "Unknown application error");
};

export function Catch() {
  const routeError = useRouteError();

  React.useEffect(() => {
    logBrowserCrash("react-router", routeError);
  }, [routeError]);

  const message = getRouteErrorMessage(routeError);
  const stack =
    routeError instanceof Error && config.isDevelopment
      ? routeError.stack
      : undefined;

  return (
    <div className="app-page-background fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center p-6">
      <div className="glass w-full max-w-xl rounded-3xl p-8 text-center">
        <h2 className="text-2xl font-bold tracking-tight">
          Unexpected Application Error!
        </h2>
        <p className="mt-3 text-[15px] text-[var(--text-secondary)] break-words">
          {message}
        </p>
        {stack && (
          <pre className="mt-6 max-h-64 overflow-auto rounded-2xl bg-[var(--bg-secondary)] p-4 text-start text-xs text-[var(--text-secondary)] whitespace-pre-wrap break-words">
            {stack}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function MyApp() {
  const { ToastContainer } = useToast();
  const _hasHydrated = useAppStore((state) => state._hasHydrated);
  const _hydrationError = useAppStore((state) => state._hydrationError);
  const _sessionExpiredAt = useAppStore((state) => state._sessionExpiredAt);
  const _telemetryUserId = useAppStore((state) => state.userData?.id);
  const _checkoutEmail = useAppStore((state) => state.userData?.email);
  const _sessionToken = useAppStore((state) => state.userToken);
  const _handledSessionExpiry = React.useRef<number | null>(null);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const _lifecycleNavigate = React.useRef(navigate);
  const _lifecycleT = React.useRef(t);
  const _lifecycleSessionToken = React.useRef(_sessionToken);
  const _lifecycleCheckoutEmail = React.useRef(_checkoutEmail);
  const _lifecycleCheckoutUserId = React.useRef(_telemetryUserId);
  _lifecycleNavigate.current = navigate;
  _lifecycleT.current = t;
  _lifecycleSessionToken.current = _sessionToken;
  _lifecycleCheckoutEmail.current = _checkoutEmail;
  _lifecycleCheckoutUserId.current = _telemetryUserId;

  // Initialize theme system - must be called unconditionally (React hooks rule)
  const { resolvedTheme } = useTheme();

  React.useEffect(() => {
    if (
      !_hasHydrated ||
      !_sessionExpiredAt ||
      _handledSessionExpiry.current === _sessionExpiredAt
    ) {
      return;
    }
    _handledSessionExpiry.current = _sessionExpiredAt;
    telemetry.clearIdentity();
    telemetry.track("session.expired", { reason: "unauthorized" });
    if (isNativeApp()) {
      // A rejected guest token should be replaced silently on native instead
      // of turning an expired device session into a required account sign-in.
      useUISlice.getState().setIsNewSession(true);
      navigate("/home");
    } else {
      navigate({ pathname: "/auth", search: "?status=logout" });
    }
  }, [_hasHydrated, _sessionExpiredAt, navigate]);

  React.useEffect(() => {
    if (!_hasHydrated) return;
    if (_telemetryUserId) telemetry.identify({ userId: _telemetryUserId });
    else telemetry.clearIdentity();
  }, [_hasHydrated, _telemetryUserId]);

  // OS-shared audio/video files ("Open with 1Transcribe"). Android delivers
  // them through the FileOpen plugin; iOS document opens arrive as file://
  // URLs on appUrlOpen. Home consumes pendingSharedFile and starts the import.
  React.useEffect(() => {
    if (!_hasHydrated) return;
    let disposed = false;
    const listeners: Array<{ remove: () => Promise<void> }> = [];

    const acceptSharedFile = (file: {
      uri: string;
      name: string;
      mimeType?: string;
      size?: number;
    }) => {
      if (disposed) return;
      useUISlice.getState().setPendingSharedFile(file);
      navigate("/home");
    };

    // Test/diagnostic hand-off mirroring the native deliveries below.
    const handleSharedFileEvent = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.uri && detail?.name) acceptSharedFile(detail);
    };
    // action-id: lifecycle.sharedFile.received
    window.addEventListener("sharedFileReceived", handleSharedFileEvent);

    if (!isNativeApp()) {
      return () => {
        disposed = true;
        window.removeEventListener("sharedFileReceived", handleSharedFileEvent);
      };
    }

    if (getPlatform() === "android") {
      void /* action-id: lifecycle.sharedFile.received */ fileOpen
        .addListener("fileOpened", (file: OpenedSharedFile) =>
          acceptSharedFile(file)
        )
        .then((listener) => {
          if (disposed) void listener.remove();
          else listeners.push(listener);
        })
        // Simulated-android/web contexts have no native FileOpen plugin.
        .catch(() => {});
      void fileOpen
        .getPendingFile()
        .then(({ file }) => {
          if (file) acceptSharedFile(file);
        })
        .catch(() => {});
    } else {
      const acceptFileUrl = (url: string) => {
        if (!url.startsWith("file://")) return;
        const name = decodeURIComponent(
          url.split("?")[0].split("/").pop() || "shared-file"
        );
        acceptSharedFile({ uri: url, name });
      };
      void runtime.app
        .onUrlOpen(({ url }) => acceptFileUrl(url))
        .then((listener) => {
          if (disposed) void listener.remove();
          else listeners.push(listener);
        });
      void runtime.app
        .getLaunchUrl()
        .then((url) => {
          if (url) acceptFileUrl(url);
        })
        .catch(() => {});
    }

    return () => {
      disposed = true;
      window.removeEventListener("sharedFileReceived", handleSharedFileEvent);
      for (const listener of listeners) void listener.remove();
    };
  }, [_hasHydrated, navigate]);

  // Ensure status bar is set after hydration on Android
  React.useEffect(() => {
    if (!_hasHydrated || getPlatform() !== "android") return;

    // Delay to ensure Capacitor bridge is ready
    const timeout = setTimeout(() => {
      updateStatusBarStyle(resolvedTheme);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [_hasHydrated, resolvedTheme]);

  React.useEffect(() => {
    if (!_hasHydrated) return;
    const search = new URLSearchParams(window.location.search);
    const checkoutSuccess = search.get("checkout_success") === "true";
    const checkoutPlan = search.get("plan") || undefined;
    const checkoutSessionId = search.get("session_id") || undefined;
    const checkoutIdempotencyKey =
      checkoutSessionId ||
      `${checkoutPlan || "unknown"}:${window.location.pathname}:${window.location.search}`;
    const clearCheckoutReturn = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout_success");
      url.searchParams.delete("plan");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    };

    return lifecycle.attach({
      navigate: (destination) => {
        if (destination.page === "home") {
          _lifecycleNavigate.current("/home");
          return;
        }
        const pathname = {
          translate: "/translate",
          summary: "/summary",
          quiz: "/quiz",
          flashcard: "/flashcard",
        } as const;
        _lifecycleNavigate.current({
          pathname: pathname[destination.page],
          search: `?${new URLSearchParams({ id: destination.transcriptId })}`,
        });
      },
      showPaywall: (utmSource) => {
        useUISlice.getState().setPaywallVisible({ utmSource });
      },
      checkout: checkoutSuccess
        ? {
            idempotencyKey: checkoutIdempotencyKey,
            isAuthenticated: Boolean(_lifecycleSessionToken.current),
            customerEmail: accountEmail({
              email: _lifecycleCheckoutEmail.current,
              id: _lifecycleCheckoutUserId.current,
            }),
            plan: checkoutPlan,
            transactionId: checkoutSessionId || checkoutIdempotencyKey,
            transcriptId:
              new URLSearchParams(window.location.search).get("id") || undefined,
            clearReturn: clearCheckoutReturn,
            showSuccess: (onContinue) => {
              useUISlice.getState().setAlertModal({
                title: _lifecycleT.current("actions.checkoutReturnTitle", {
                  defaultValue: "Payment received",
                }),
                subtitle: _lifecycleT.current(
                  "actions.checkoutReturnDescription",
                  {
                    defaultValue:
                      "Confirming your subscription and unlocking Premium.",
                  },
                ),
                hideDismissIcon: true,
                shouldPersistBackdropClick: true,
                button: {
                  text: _lifecycleT.current("actions.subscriptionSuccessButton"),
                  onPress: onContinue,
                },
              });
            },
            setSuccessLoading: () => {
              const modal = useUISlice.getState().alertModal;
              if (!modal) {
                throw new Error("Cannot mark a missing success modal as loading");
              }
              useUISlice.getState().setAlertModal({
                ...modal,
                loading: true,
                button: modal.button && {
                  ...modal.button,
                  text: _lifecycleT.current("home.loading"),
                },
              });
            },
            showRetry: (onRetry) => {
              useUISlice.getState().setAlertModal({
                title: _lifecycleT.current("actions.checkoutSyncTitle", {
                  defaultValue: "Your payment is still syncing",
                }),
                subtitle: _lifecycleT.current(
                  "actions.checkoutSyncDescription",
                  {
                    defaultValue:
                      "Keep this page open and try again. Your transcript is safe.",
                  },
                ),
                hideDismissIcon: true,
                shouldPersistBackdropClick: true,
                button: {
                  text: _lifecycleT.current("actions.checkoutSyncButton", {
                    defaultValue: "Try again",
                  }),
                  onPress: onRetry,
                },
              });
            },
            closeSuccess: () => {
              useUISlice.getState().setAlertModal(undefined);
            },
            onCompleted: async () => {
              const result = await actions.user.get();
              useAuth.protectGuestPurchase({
                email: "error" in result ? undefined : result.value.unverifiedEmail,
                callback: (authToken) => {
                  void actions.user.profile();
                  // The account now carries the purchase, so every app can
                  // open it: send the buyer to the one this device installs.
                  const email = accountEmail(useAuth.currentAuthUser(authToken));
                  useUISlice.getState().setAlertModal({
                    title: _lifecycleT.current("actions.getAppsTitle"),
                    subtitle: email
                      ? _lifecycleT.current("actions.getAppsSubtitle", { email })
                      : _lifecycleT.current("actions.getAppsSubtitleNoEmail"),
                    button: {
                      text: _lifecycleT.current("actions.getAppsButton"),
                      onPress: () => {
                        useUISlice.getState().setAlertModal(undefined);
                        const platform = getDeviceAppPlatform({
                          userAgent: navigator.userAgent,
                          maxTouchPoints: navigator.maxTouchPoints,
                        });
                        logClientEvent("acquisition.downloadStarted", {
                          destination: platform || "apps",
                          surface: "purchaseComplete",
                        });
                        window.open(
                          platform
                            ? APP_DOWNLOAD_URLS[platform]
                            : getAppsWebUrl(),
                          "_blank",
                        );
                      },
                    },
                    cancelText: _lifecycleT.current("actions.getAppsContinueWeb"),
                  });
                },
              });
            },
          }
        : undefined,
    });
  }, [_hasHydrated]);

  React.useEffect(() => {
    let mounted = true;
    let resizeTimeout: NodeJS.Timeout;

    // More reliable device state update with error handling
    const updateDeviceState = () => {
      if (!mounted) return;

      try {
        const newState = {
          isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
          width: window.innerWidth,
          height: window.innerHeight,
        };

        // Only update if values actually changed
        const currentDevice = useUISlice.getState().device;
        if (
          currentDevice.width !== newState.width ||
          currentDevice.height !== newState.height ||
          currentDevice.isMobile !== newState.isMobile
        ) {
          useUISlice.getState().setDevice(newState);
        }

        // Mark as initialized after first successful update
        if (!isInitialized && mounted) {
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Failed to update device state:", error);
        // Still mark as initialized to prevent blocking
        if (!isInitialized && mounted) {
          setIsInitialized(true);
        }
      }
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDeviceState, 100);
    };

    const handlePopState = () => {
      // Load analytics with error handling
      loadAnalytics();
    };

    // Initial setup - run immediately for reliability
    updateDeviceState();

    // Load analytics after initial render
    requestAnimationFrame(() => {
      loadAnalytics();
    });

    const detachResize = attachViewportResize(window, handleResize);
    const detachPopState = attachBrowserBack(window, handlePopState);

    return () => {
      mounted = false;
      clearTimeout(resizeTimeout);
      detachPopState();
      detachResize();
    };
  }, [isInitialized]);

  if (_hydrationError) {
    return (
      <div className="app-page-background fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center p-6">
        <div className="glass w-full max-w-xl rounded-3xl p-8 text-center">
          <h2 className="text-2xl font-bold tracking-tight">
            Saved data could not be loaded
          </h2>
          <p className="mt-3 text-[15px] text-[var(--text-secondary)]">
            Your saved data was left unchanged. Close and reopen the app to try
            again.
          </p>
        </div>
      </div>
    );
  }

  if (!_hasHydrated) return <div />;

  const desktopSignInRequired =
    desktop.isAvailable &&
    !hasAccountIdentity(useAuth.currentAuthUser(_sessionToken));

  const appContent = (
    <DesktopHostProvider>
      <DesktopRuntimeBridge />
      {desktopSignInRequired && pathname !== "/desktop-sign-in" ? (
        <Navigate to="/desktop-sign-in" replace />
      ) : (
        <Outlet />
      )}
      {desktop.isAvailable && (
        <React.Suspense fallback={null}>
          <DictationLauncher />
        </React.Suspense>
      )}
      <AlertWidgetCreateTranscriptFromFileImport />
      <AlertWidgetCreateTranscriptVoiceRecording />
      <AlertExportTranscriptOptions />
      <AlertTagPicker />
      <AlertWidgetSearch />
      <AlertLanguagePicker />
      <AlertPrompt />
      <AlertModal />
      <AlertLargeFileUpload />
      <AlertPromptSignIn />
      <AlertWidgetPaywall />
      {/* Native-only. Mounting on web would still run the component's
          effects even though it renders null — its appStateChange
          listener maps to tab visibility on web and used to emit bogus
          onboarding activity from web sessions. */}
      {isNativeApp() && <AlertWidgetOnboarding />}
      <AlertFlowSessionArmed />
      <ToastContainer />
    </DesktopHostProvider>
  );

  return (
    <React.Suspense>
      {isNativeApp() ? (
        appContent
      ) : (
        <GoogleOAuthProvider clientId={config.googleClientId}>
          {appContent}
        </GoogleOAuthProvider>
      )}
    </React.Suspense>
  );
}
